import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { uploadAudioFile, addFileToCollection } from '../services/api';
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from './AuthContext';
import { trackEvent } from '../services/analytics';

export interface FileUploadItem {
  id: string;
  file: File;
  status: 'queued' | 'uploading' | 'processing' | 'completed' | 'failed';
  progress: number;
  result?: {
    uploadId: string;
    shareUrl: string;
  };
  error?: string;
  metadata?: {
    title?: string;
    description?: string;
    sourceType?: 'upload' | 'recording';
    // When set, the file is auto-attached to this collection after upload.
    collectionId?: string;
    // Pro/Beta opt-in: keep the user's original source file (WAV / FLAC /
    // AIF / etc.) on S3 in addition to the transcoded MP3. Front-end sends
    // `keep_original: true` in the presigned-URL request body when set.
    // Backend (audio Lambda) honors the flag by skipping the post-transcode
    // delete and writing a sentinel on the file record; until the backend
    // ships it ignores the field (additive contract).
    keepOriginal?: boolean;
    // Backend determines permanent storage based on JWT plan claim
  };
  // Soft warning when upload succeeded but a follow-up (e.g. attach-to-collection) failed.
  warning?: string;
  // True once the file has been successfully attached to metadata.collectionId.
  // Watchers should wait for this — not just 'completed' — before treating the
  // file as visible in the target collection (the LIST fetch needs the POST to
  // have committed first).
  attached?: boolean;
}

interface UploadContextType {
  queue: FileUploadItem[];
  isUploading: boolean;

  // Queue management
  addFiles: (files: File[], metadata?: Partial<FileUploadItem['metadata']>) => void;
  removeFile: (id: string) => void;
  clearCompleted: () => void;
  clearAll: () => void;
  retryFile: (id: string) => void;

  // Upload control
  startQueue: () => void;
  pauseQueue: () => void;

  // Session-level Keep-Original opt-in (Pro/Beta gated UI). Owned by the
  // context so the checkbox can live in any pre-select surface (UploadZone
  // today, future call sites tomorrow) and `addFiles` injects the flag
  // into each new item's metadata without every caller threading it
  // through. Resets to false on provider mount; not persisted across reloads.
  keepOriginalNext: boolean;
  setKeepOriginalNext: (next: boolean) => void;

  // Statistics
  totalFiles: number;
  completedFiles: number;
  failedFiles: number;

  // Overall progress helper
  getOverallProgress: () => number;
}

const UploadContext = createContext<UploadContextType | undefined>(undefined);

export const UploadProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { getValidToken, isProUser, isBetaUser } = useAuth();
  const [queue, setQueue] = useState<FileUploadItem[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  // Session-level Keep-Original preference. Default off. The checkbox UI
  // is gated on Pro/Beta at the consumer (UploadZone); the context doesn't
  // gate the setter — it just stores whatever the consumer writes. That
  // keeps the context dumb and lets gating policy live with the UI.
  const [keepOriginalNext, setKeepOriginalNext] = useState<boolean>(false);
  const keepOriginalNextRef = useRef<boolean>(false);
  const isPausedRef = useRef(false);
  const processingRef = useRef(false);
  const queueRef = useRef<FileUploadItem[]>([]);

  // Mirror keepOriginalNext into a ref so addFiles (a stable useCallback)
  // can read the current value without taking it as a dep — otherwise every
  // toggle would invalidate addFiles and ripple through every consumer that
  // memoizes on it.
  useEffect(() => {
    keepOriginalNextRef.current = keepOriginalNext;
  }, [keepOriginalNext]);

  // Keep queueRef in sync with queue state
  useEffect(() => {
    queueRef.current = queue;
  }, [queue]);

  const processQueue = useCallback(async () => {
    if (processingRef.current || isPausedRef.current) {
      return;
    }

    processingRef.current = true;
    setIsProcessing(true);

    // Process items sequentially
    while (!isPausedRef.current) {
      // Find next queued item by reading from ref (synchronous, no React batching issues)
      const nextItem = queueRef.current.find(item => item.status === 'queued');

      if (!nextItem) {
        break; // No more items to process
      }

      // TypeScript: nextItem is guaranteed to be defined here
      const currentItem = nextItem;

      // Update status to uploading
      setQueue(prev => prev.map(item =>
        item.id === currentItem.id
          ? { ...item, status: 'uploading', progress: 10 }
          : item
      ));

      try {
        // Get valid auth token (if user is authenticated)
        const idToken = await getValidToken();

        // Upload the file
        const response = await uploadAudioFile(
          currentItem.file,
          (progress) => {
            // Update progress
            setQueue(prev => prev.map(item =>
              item.id === currentItem.id
                ? { ...item, progress }
                : item
            ));
          },
          currentItem.metadata?.title,
          currentItem.metadata?.description,
          currentItem.metadata?.sourceType || 'upload', // Default to 'upload' if not specified
          idToken || undefined, // Pass auth token if available (backend decides permanent storage)
          currentItem.metadata?.keepOriginal === true, // Pro/Beta opt-in; backend ignores until it lands
        );

        // Mark as completed
        setQueue(prev => prev.map(item =>
          item.id === currentItem.id
            ? {
                ...item,
                status: 'completed',
                progress: 100,
                result: {
                  uploadId: response.fileId,
                  shareUrl: response.shareUrl,
                },
              }
            : item
        ));

        // Auto-attach to a collection if requested by the caller. The upload itself
        // is already done — if attach fails we keep the file as completed and
        // surface a soft warning so the user can retry from the collection UI.
        const targetCollectionId = currentItem.metadata?.collectionId;
        if (targetCollectionId && idToken) {
          try {
            await addFileToCollection(response.fileId, targetCollectionId, idToken);
            // Flip attached=true ONLY after the POST has resolved, so a listener
            // refetching the collection list will actually see this file in it.
            setQueue(prev => prev.map(item =>
              item.id === currentItem.id
                ? { ...item, attached: true }
                : item
            ));
          } catch (attachErr) {
            const attachMsg = attachErr instanceof Error ? attachErr.message : 'Could not add to collection';
            setQueue(prev => prev.map(item =>
              item.id === currentItem.id
                ? { ...item, warning: attachMsg }
                : item
            ));
          }
        }

        // Store claim token in localStorage for anonymous uploads (no auth token)
        if (!idToken && response.claimToken) {
          try {
            // Get existing claim tokens from localStorage
            const existingTokensStr = localStorage.getItem('pendingClaims');
            const existingTokens = existingTokensStr ? JSON.parse(existingTokensStr) : [];

            // Add new claim token (max 24 hour validity, stored with timestamp)
            const newClaim = {
              fileId: response.fileId,
              claimToken: response.claimToken,
              fileName: currentItem.file.name,
              timestamp: Date.now(),
            };

            existingTokens.push(newClaim);

            // Clean up expired tokens (older than 24 hours)
            const validTokens = existingTokens.filter((claim: any) =>
              Date.now() - claim.timestamp < 24 * 60 * 60 * 1000
            );

            localStorage.setItem('pendingClaims', JSON.stringify(validTokens));
            console.log('Stored claim token for anonymous upload:', response.fileId);
          } catch (error) {
            console.error('Failed to store claim token:', error);
            // Non-critical error - upload still succeeded
          }
        }
      } catch (error) {
        // Mark as failed
        const errorMessage = error instanceof Error ? error.message : 'Upload failed';
        setQueue(prev => prev.map(item =>
          item.id === currentItem.id
            ? { ...item, status: 'failed', error: errorMessage }
            : item
        ));
      }

      // Small delay between uploads to prevent throttling
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    processingRef.current = false;
    setIsProcessing(false);
  }, [getValidToken]); // Include getValidToken since we call it

  const addFiles = useCallback((files: File[], metadata?: Partial<FileUploadItem['metadata']>) => {
    // Determine max file size based on user plan
    // Free: 50MB, Pro/Beta: 1500MB
    const isPro = isProUser() || isBetaUser();
    const maxFileSizeBytes = isPro ? 1500 * 1024 * 1024 : 50 * 1024 * 1024;
    const maxFileSizeMB = isPro ? 1500 : 50;
    const planName = isPro ? 'Pro' : 'Free';

    // Inject the session-level Keep-Original flag into each new item unless
    // the caller already provided an explicit value (caller wins). Free users
    // never get the flag because the UI that sets it is gated behind Pro/Beta;
    // we belt-and-braces here so a stale ref or a renamed gate elsewhere can
    // never sneak the flag onto a free-tier upload.
    const effectiveKeepOriginal =
      isPro && (metadata?.keepOriginal ?? keepOriginalNextRef.current);
    const effectiveMetadata: Partial<FileUploadItem['metadata']> | undefined =
      effectiveKeepOriginal || metadata
        ? { ...(metadata || {}), keepOriginal: effectiveKeepOriginal || undefined }
        : undefined;

    const newItems: FileUploadItem[] = files.map(file => {
      // Validate file size
      if (file.size > maxFileSizeBytes) {
        // Fire-and-forget telemetry so we can measure how many uploads we're
        // refusing at the free-tier ceiling. Server derives auth dimension
        // from the (possibly absent) Bearer token; one beacon per rejected
        // file — no aggregation across a multi-file drop.
        trackEvent('upload_rejected_too_large', getValidToken, { allowAnon: true });
        return {
          id: uuidv4(),
          file,
          status: 'failed' as const,
          progress: 0,
          metadata: effectiveMetadata,
          error: `File too large. ${planName} plan limit: ${maxFileSizeMB}MB (file is ${Math.round(file.size / (1024 * 1024))}MB)`,
        };
      }

      return {
        id: uuidv4(),
        file,
        status: 'queued' as const,
        progress: 0,
        metadata: effectiveMetadata,
      };
    });

    setQueue(prev => [...prev, ...newItems]);

    // Auto-start processing if not already processing
    if (!processingRef.current && !isPausedRef.current) {
      setTimeout(() => processQueue(), 100);
    }
  }, [processQueue, isProUser, isBetaUser, getValidToken]);

  const removeFile = useCallback((id: string) => {
    setQueue(prev => prev.filter(item => item.id !== id));
  }, []);

  const clearCompleted = useCallback(() => {
    setQueue(prev => prev.filter(item => item.status !== 'completed'));
  }, []);

  const clearAll = useCallback(() => {
    isPausedRef.current = true;
    setQueue([]);
  }, []);

  const retryFile = useCallback((id: string) => {
    setQueue(prev => prev.map(item =>
      item.id === id
        ? { ...item, status: 'queued', progress: 0, error: undefined }
        : item
    ));

    // Auto-start processing if not already processing
    if (!processingRef.current && !isPausedRef.current) {
      setTimeout(() => processQueue(), 100);
    }
  }, [processQueue]);

  const startQueue = useCallback(() => {
    isPausedRef.current = false;
    if (!processingRef.current) {
      processQueue();
    }
  }, [processQueue]);

  const pauseQueue = useCallback(() => {
    isPausedRef.current = true;
  }, []);

  // Auto-trigger processing when queued items appear
  const queuedCount = queue.filter(item => item.status === 'queued').length;

  useEffect(() => {
    const isNotProcessing = !processingRef.current && !isPausedRef.current;

    if (queuedCount > 0 && isNotProcessing) {
      // Use setTimeout to avoid triggering during render
      const timer = setTimeout(() => {
        processQueue();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [queuedCount, processQueue]); // Trigger when count of queued items changes

  // Statistics
  const totalFiles = queue.length;
  const completedFiles = queue.filter(item => item.status === 'completed').length;
  const failedFiles = queue.filter(item => item.status === 'failed').length;

  // Overall progress calculation
  const getOverallProgress = useCallback(() => {
    if (queue.length === 0) return 0;
    const totalProgress = queue.reduce((sum, item) => sum + item.progress, 0);
    return Math.round(totalProgress / queue.length);
  }, [queue]);

  return (
    <UploadContext.Provider
      value={{
        queue,
        isUploading: isProcessing,
        addFiles,
        removeFile,
        clearCompleted,
        clearAll,
        retryFile,
        startQueue,
        pauseQueue,
        keepOriginalNext,
        setKeepOriginalNext,
        totalFiles,
        completedFiles,
        failedFiles,
        getOverallProgress,
      }}
    >
      {children}
    </UploadContext.Provider>
  );
};

export const useUpload = () => {
  const context = useContext(UploadContext);
  if (context === undefined) {
    throw new Error('useUpload must be used within an UploadProvider');
  }
  return context;
};
